import { useRef } from "react";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import AccessTime from "@mui/icons-material/AccessTime";
import { useTranslation } from "react-i18next";

/**
 * Native `::-webkit-calendar-picker-indicator` ignores many global styles in Chromium.
 * We hide it and use a theme-colored clock + `showPicker()` so dark/light mode always looks correct.
 */
export default function TimeTextField({ InputProps, sx, ...rest }: TextFieldProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  function openPicker() {
    const el = inputRef.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === "function") {
        el.showPicker();
      } else {
        el.focus();
      }
    } catch {
      el.focus();
    }
  }

  const hideNativeClock: NonNullable<TextFieldProps["sx"]> = {
    "& input[type='time']::-webkit-calendar-picker-indicator": {
      display: "none",
    },
  };

  const clockAdornment = (
    <InputAdornment position="end">
      <IconButton
        size="small"
        edge="end"
        tabIndex={-1}
        aria-label={t("common.chooseTime")}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          openPicker();
        }}
      >
        <AccessTime fontSize="small" sx={{ color: "text.secondary" }} />
      </IconButton>
    </InputAdornment>
  );

  const sxExtra = Array.isArray(sx) ? sx : sx != null ? [sx] : [];

  return (
    <TextField
      {...rest}
      type="time"
      inputRef={inputRef}
      sx={[hideNativeClock, ...sxExtra]}
      InputProps={{
        ...InputProps,
        endAdornment: (
          <>
            {InputProps?.endAdornment}
            {clockAdornment}
          </>
        ),
      }}
    />
  );
}
